import { Col, Collapse, Row, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as RecebaAdiantamentoIcon } from "shared/assets/images/receba-adiantamento-icon.svg";
import { ReactComponent as MigracaoCatalogoIcon } from "shared/assets/images/migracao-catalogo-icon.svg";
import { ReactComponent as AssinaturaDigitalIcon } from "shared/assets/images/assinatura-digital-icon.svg";
import { ReactComponent as Music } from "shared/assets/images/music.svg";
import { ReactComponent as MusicNote } from "shared/assets/images/music-note.svg";
import {
  changeStatusCashAdvanceProcess,
  putUpdateQuoteSelected,
} from "shared/service/StrmService";
import { AdvanceStatus } from "shared/enums/AdvanceStatus";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";
import moment from "moment";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { useSelector } from "react-redux";
import { AdvanceButtonClickEventName } from "event-tracker/button-click-event-name";
import { moneyFormat, ptBRFormat } from "shared/helpers/BRLFormat";
import { CashAdvanceRelease } from "shared/interfaces/CashAdvanceRelease";
import {
  CashAdvanceQuoteTypeEnum,
  CashAdvanceSummary,
} from "shared/interfaces/CashAdvanceSummary";
import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import "./AdiantamentoAceite.scss";
import { useFlags } from "launchdarkly-react-client-sdk";
import { toast } from "react-toastify";

export enum StepAceite {
  Assinatura = 0,
  Migracao = 1,
  Recebimento = 2,
}

const calculaPorcentagem = (valor: number) => {
  return valor * 100;
};

const AdiantamentoAceite: React.FC<any> = (props) => {
  const { advance, reloadCashAdvance } = props;
  const [showSteps, setShowSteps] = useState<boolean>(false);
  const [step, setStep] = useState<number>(StepAceite.Assinatura);
  const [exibeLancamentos, setExibeLancamentos] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { multipliesAdvanceQuotes } = useFlags();

  const { t } = useTranslation();
  const faqList: any[] = t("pages.Adiantamentos.faqAceite");
  const { userArtist } = useSelector((state: any) => state.artists);
  const showQuotes = useMemo(() => {
    return (
      multipliesAdvanceQuotes &&
      advance?.advanceQuotes &&
      advance.advanceQuotes.length > 0
    );
  }, [multipliesAdvanceQuotes, advance]);

  const getStyleByStep = (propStep: StepAceite) => {
    if (step !== undefined) {
      switch (propStep) {
        case StepAceite.Migracao:
          if (step > StepAceite.Assinatura) return "complete";
          return "waiting";

        case StepAceite.Recebimento:
          if (step > StepAceite.Migracao) return "complete";
          return "waiting";
      }
    }
    return "";
  };

  const moveStep = async () => {
    dataLayerClickedEvent(AdvanceButtonClickEventName.NEXT, {
      ...tagManagerEvent.Advance.S4A_ADVANCE_CONFIRMATION,
      carreerStage: userArtist?.stage,
      proposalValue: advance?.totalAmountUsd,
      approvedLimit: userArtist?.cashAdvanceEstimated?.amountUsd,
      alreadyCalculated:
        userArtist?.cashAdvanceEstimated?.successfullyEstimated,
      country: userArtist?.country,
    });
    await changeStatusCashAdvanceProcess(
      advance.id,
      AdvanceStatus.DocumentationForContract,
    );
    reloadCashAdvance();
  };

  const moveNext = () => {
    if (step === StepAceite.Recebimento) moveStep();
    else setStep(step + 1);
  };

  const movePrev = () => {
    if (step === StepAceite.Assinatura) {
      setShowSteps(false);
    } else {
      setStep(step - 1);
    }
  };

  const onClickBackStep = async () => {
    await changeStatusCashAdvanceProcess(
      advance.id,
      AdvanceStatus.ReleasesSelection,
    );
    reloadCashAdvance();
  };

  const onClickAcceptProposal = async (item: CashAdvanceSummary) => {
    try {
      setLoading(true);
      await putUpdateQuoteSelected(item.id!);
      reloadCashAdvance();
    } catch (error) {
      console.error("Error updating quote:", error);
      toast.error<string>(t("pages.Adiantamentos.erroAceitarProposta"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (advance?.totalAmountUsd) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Advance.S4A_ADVANCE_PROPOSAL,
        proposalValue: advance?.totalAmountUsd,
      });
    }
  }, [advance]);

  const sortedQuotes: CashAdvanceSummary[] =
    multipliesAdvanceQuotes &&
    advance?.advanceQuotes &&
    advance.advanceQuotes.length > 0
      ? (advance.advanceQuotes || [])
          .slice()
          .sort(
            (a: CashAdvanceSummary, b: CashAdvanceSummary) =>
              (b.totalAmount || 0) - (a.totalAmount || 0),
          )
      : [];

  const getLabel = (type: CashAdvanceQuoteTypeEnum) => {
    if (type === 0) return "Valor maior";
    if (type === 1) return "Valor médio";
    if (type === 2) return "valor menor";
    return undefined;
  };

  const RenderResumo: React.FC<{
    data: CashAdvanceSummary;
    tag?: React.ReactNode;
    showAcceptButton?: boolean;
  }> = ({ data, tag, showAcceptButton }) => {
    return (
      <div className="resumo">
        <div className={`linha ${data?.currency === "BRL" ? "borda" : ""}`}>
          {tag}
          <span className="valor">
            {t("pages.Adiantamentos.totalAdiantamento")}
            <br />
            {(data?.trancheFactor || 0) > 1 && (
              <b
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: "#b3b3b3",
                }}
              >
                {t("pages.Adiantamentos.valoresEmTranches")}
              </b>
            )}
          </span>
          <span className="valor-total">
            {moneyFormat(
              data.totalAmount || data.amount,
              data?.currency || "BRL",
            )}
            <span hidden={data?.currency === "BRL"}> *</span>
          </span>
        </div>
        <div
          className="linha borda disclaimer-valor-adiantamento"
          hidden={data?.currency === "BRL"}
        >
          <span>
            <span className="asterisk">*</span>{" "}
            {t("pages.Adiantamentos.valorAdiantamentoDisclaimer")}
          </span>
        </div>

        <div className="percentual-disclaimer">
          <span>{t("pages.Adiantamentos.aceitePercentual")}</span>
          <Tooltip title={t("pages.Adiantamentos.aceitePercentualDisclaimer")}>
            <InfoCircleOutlined />
          </Tooltip>
        </div>
        <div className="acordo">
          <div>
            <span className="cinza">
              {t("pages.Adiantamentos.ateQuitacao")}
            </span>
            <span className="branco">
              {t("pages.Adiantamentos.percentualAritsta", {
                percentual: calculaPorcentagem(
                  data.artistPercentageUntilDischarge || 0,
                ),
              })}
            </span>
            <span className="cinza" style={{ fontSize: 11 }}>
              {t("pages.Adiantamentos.utilizadoParaQuitacao")}
            </span>
          </div>
          <div style={{ justifyContent: "flex-end" }}>
            <span className="branco">
              {calculaPorcentagem(data.strmPercentageUntilDischarge || 0)}
              {"% "}
              Strm
            </span>
            <span className="cinza" style={{ fontSize: 11 }}>
              {t("pages.Adiantamentos.feeDistribuicao")}
            </span>
          </div>
        </div>

        <div className="acordo">
          <div>
            <span className="cinza">
              {t("pages.Adiantamentos.aposQuitacao")}
            </span>
            <span className="branco">
              {t("pages.Adiantamentos.percentualAritsta", {
                percentual: calculaPorcentagem(
                  data.artistPercentageAfterDischarge || 0,
                ),
              })}
            </span>
            <span className="cinza" style={{ fontSize: 11 }}>
              {t("pages.Adiantamentos.validoFinalContrato")}
            </span>
          </div>
          <div style={{ justifyContent: "flex-end" }}>
            <span className="branco">
              {calculaPorcentagem(data.strmPercentageAfterDischarge || 0)}
              {"% "}
              Strm
            </span>
            <span className="cinza" style={{ fontSize: 11 }}>
              {t("pages.Adiantamentos.feeDistribuicao")}
            </span>
          </div>
        </div>

        <div className="linha borda">
          <span>{t("pages.Adiantamentos.quantidadeMinimaLancamentos")}</span>
          <span className="valor" style={{ flex: 1, textAlign: "right" }}>
            {t("pages.Adiantamentos.quantidadeMinimaLancamentosCount", {
              lancamentos: data.minimumNumberOfReleasesRequired,
            })}
          </span>
          <Tooltip
            title={t(
              "pages.Adiantamentos.quantidadeMinimaLancamentosDisclaimer",
            )}
          >
            <InfoCircleOutlined style={{ marginLeft: 6 }} />
          </Tooltip>
        </div>
        <div className="linha">
          <span>{t("pages.Adiantamentos.prazo")}</span>
          <span className="valor" style={{ flex: 1, textAlign: "right" }}>
            {t("pages.Adiantamentos.prazoUmAno")}
          </span>
        </div>
        <div style={{ margin: "24px 0", width: "100%" }}>
          <button
            className="botao-roxo continuar"
            hidden={!showAcceptButton}
            style={{ width: "100%", position: "relative" }}
            onClick={() => onClickAcceptProposal(data)}
            disabled={loading}
          >
            {loading && (
              <span
                style={{
                  fontSize: 25,
                  position: "absolute",
                  left: "50%",
                  transform: " translate(-50%)",
                }}
              >
                <LoadingOutlined spin />
              </span>
            )}
            <span>{t("pages.Adiantamentos.aceitarProposta")}</span>
          </button>
        </div>
      </div>
    );
  };

  if (showSteps)
    return (
      <>
        <div className="aceite-adiantamento">
          <div
            className="flex-column-between"
            style={{ maxWidth: isMobile ? "unset" : 650 }}
          >
            <div style={{ marginTop: 16, flex: 1 }}>
              <div className="stepper" style={{ marginBottom: 16 }}>
                <div className="step complete">
                  <div className="step-circle">
                    <div className="handler" />
                  </div>
                </div>
                <div className={`step ${getStyleByStep(StepAceite.Migracao)}`}>
                  <div className="step-circle">
                    <div className="handler" />
                  </div>
                </div>
                <div
                  className={`step ${getStyleByStep(StepAceite.Recebimento)}`}
                >
                  <div className="step-circle">
                    <div className="handler" />
                  </div>
                </div>
              </div>

              <h3 style={{ color: "#fff", textAlign: "center" }}>
                {t("pages.Adiantamentos.proximosPassos")}
              </h3>
              {step === StepAceite.Assinatura && (
                <div className="carousel-content">
                  <AssinaturaDigitalIcon />
                  <h1>{t("pages.Adiantamentos.assinarDigitalmente")}</h1>

                  <p className="text">
                    <Trans
                      i18nKey={t(
                        "pages.Adiantamentos.enviarRelatorioReceitaPassoDois",
                      )}
                    />
                  </p>
                </div>
              )}
              {step === StepAceite.Migracao && (
                <>
                  <div className="carousel-content">
                    <MigracaoCatalogoIcon />
                    <h1>{t("pages.Adiantamentos.migrarCatalogo")}</h1>

                    <p className="text">
                      <Trans
                        i18nKey={t(
                          "pages.Adiantamentos.enviarRelatorioReceitaPassoTres",
                        )}
                      />
                    </p>
                  </div>
                </>
              )}
              {step === StepAceite.Recebimento && (
                <div>
                  <div className="carousel-content">
                    <RecebaAdiantamentoIcon />
                    <h1>{t("pages.Adiantamentos.receberAdiantamento")}</h1>

                    <p className="text">
                      <Trans
                        i18nKey={t(
                          "pages.Adiantamentos.enviarRelatorioReceitaPassoQuatro",
                        )}
                      />
                    </p>
                  </div>
                </div>
              )}
            </div>
            {step === StepAceite.Migracao && (
              <div
                style={{
                  margin: isMobile ? "50px 18px 0 18px" : "50px 0 125px 0",
                  textAlign: "center",
                }}
              >
                <p>
                  <strong style={{ color: "#5A57FF" }}>*</strong>{" "}
                  {t("pages.Adiantamentos.migracaoDisclaimer")}
                </p>
                <Collapse
                  bordered={false}
                  className="fake-collapse"
                  expandIcon={() => <FontAwesomeIcon icon={faChevronDown} />}
                  expandIconPosition="end"
                >
                  <Collapse.Panel
                    header={
                      <span
                        style={{
                          display: "block",

                          color: "#F2F2F2",
                        }}
                      >
                        {t("pages.Adiantamentos.faqTitle")}
                      </span>
                    }
                    key={1}
                  >
                    <Collapse
                      bordered={false}
                      className="faq"
                      expandIcon={() => (
                        <FontAwesomeIcon icon={faChevronDown} />
                      )}
                      expandIconPosition="end"
                    >
                      {faqList?.map((faq, i) => (
                        <Collapse.Panel
                          header={t(`pages.Adiantamentos.faq.${faq}.pergunta`)}
                          key={i}
                        >
                          <p>
                            <Trans
                              i18nKey={t(
                                `pages.Adiantamentos.faq.${faq}.resposta`,
                              )}
                            />
                          </p>
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                  </Collapse.Panel>
                </Collapse>
              </div>
            )}
            {step === StepAceite.Recebimento && (
              <div
                style={{
                  margin: isMobile ? "50px 18px 0 18px" : "50px 0 125px 0",
                  textAlign: "center",
                }}
              >
                <h3>{t(`pages.Adiantamentos.faq.takedown.pergunta`)}</h3>
                <p>
                  {" "}
                  <strong style={{ color: "#5A57FF" }}>*</strong>{" "}
                  {t(`pages.Adiantamentos.faq.takedown.resposta`)}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="footer black" style={{ paddingLeft: 0 }}>
          <div
            style={{
              display: "flex",
              width: isMobile ? "100%" : 650,
              flexDirection: "column",
              gap: 12,
            }}
          >
            <button className="botao-roxo continuar" onClick={moveNext}>
              {t("pages.Adiantamentos.continuar")}
            </button>
            <button className="botao-borda-roxa" onClick={movePrev}>
              {t("pages.Adiantamentos.voltar")}
            </button>
          </div>
        </div>
      </>
    );

  return (
    <>
      <div className="aceite-adiantamento">
        <div
          className="flex-column-between"
          style={{
            maxWidth: isMobile ? "unset" : 650,
            paddingTop: isMobile ? 0 : 60,
          }}
        >
          <div style={{ padding: "0 12px", marginBottom: isMobile ? 0 : 120 }}>
            <div className="confirma-adiantamento-intro">
              <h1>{t("pages.Adiantamentos.valoresAlteradosTitulo")}</h1>
              <p
                style={{ maxWidth: isMobile ? "unset" : 550, marginBottom: 48 }}
              >
                <Trans
                  i18nKey={t("pages.Adiantamentos.valoresAlteradosTexto")}
                />
              </p>
            </div>
            <h3 className="heading-h5">
              {t("pages.Adiantamentos.propostasTitulo")}
            </h3>
            <div className="meu-adiantamento">
              <div
                style={{ maxWidth: isMobile ? "unset" : 650, width: "100%" }}
              >
                {showQuotes ? (
                  <Collapse
                    bordered={false}
                    className="summary-collapse"
                    expandIconPosition="end"
                  >
                    {sortedQuotes.map(
                      (item: CashAdvanceSummary, idx: number) => (
                        <Collapse.Panel
                          header={
                            <div>
                              {getLabel(item.type!) && (
                                <span
                                  className={`quote-tag body-large ${item.type === CashAdvanceQuoteTypeEnum.High ? "main" : ""}`}
                                >
                                  {getLabel(item.type!)}
                                </span>
                              )}
                              <span
                                style={{
                                  display: "block",
                                  color: "#F2F2F2",
                                  fontSize: 18,
                                  marginTop: 8,
                                }}
                              >
                                {moneyFormat(
                                  item.totalAmount || item.amount,
                                  item?.currency || "BRL",
                                )}
                              </span>
                            </div>
                          }
                          key={`advance_quote_${idx}`}
                        >
                          <RenderResumo
                            data={{ ...item, ...advance }}
                            showAcceptButton
                          />
                        </Collapse.Panel>
                      ),
                    )}
                  </Collapse>
                ) : (
                  <RenderResumo data={advance} />
                )}

                <hr
                  style={{
                    border: "none",
                    height: 1,
                    backgroundColor: "#333",
                    margin: "32px 0",
                  }}
                />
                <h3 className="heading-h5">
                  {t("pages.Adiantamentos.lancamentos")}
                </h3>
                <div
                  className={`lancamentos ${exibeLancamentos && "open"}`}
                  onClick={() => setExibeLancamentos(!exibeLancamentos)}
                >
                  <Music className="svg-stroke-roxo no-bg" />
                  <span>
                    {t("pages.Adiantamentos.lancamentosSelecionadosCount", {
                      lancamentos: advance.selectedReleases?.length,
                    })}
                  </span>
                  {exibeLancamentos ? (
                    <FontAwesomeIcon icon={faChevronUp} />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} />
                  )}
                </div>

                {exibeLancamentos &&
                  advance.selectedReleases.map(
                    (release: CashAdvanceRelease) => (
                      <div
                        key={release.releaseId}
                        className="card-album"
                        style={{ marginBottom: 16 }}
                      >
                        <Row gutter={16}>
                          <Col
                            xs={8}
                            className="album-imagem"
                            style={{ marginLeft: 16 }}
                          >
                            <img src={release.releaseImageUrl} alt="" />
                          </Col>
                          <Col
                            xs={13}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              color: "8E8E8E",
                            }}
                          >
                            {t(`pages.Adiantamentos.${release.releaseType}`)}

                            <span style={{ marginBottom: 4 }}>
                              {release.releaseName}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                gap: 16,
                                alignItems: "center",
                              }}
                            >
                              <div className="flex-gap-8">
                                <div className="flex-gap-8">
                                  <FontAwesomeIcon
                                    icon={faSpotify}
                                    style={{ width: 20, height: 20 }}
                                  />
                                  <b>{ptBRFormat(release.playCount || 0)}</b>
                                  <span className="play">
                                    {t("pages.Adiantamentos.plays")}
                                  </span>
                                </div>
                                <div className="flex-gap-8">
                                  <MusicNote />

                                  <b>{release?.tracks?.length}</b>
                                  <span className="play">
                                    {t("pages.Adiantamentos.faixas")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: 16,
                          }}
                        >
                          <MusicNote />
                          {t("pages.Adiantamentos.porcentagemFaturamento")}
                        </div>

                        {release.tracks.map((track) => (
                          <div className="linha-track" key={track.trackId}>
                            <span
                              style={{
                                flex: 1,
                              }}
                              className="ellipsis"
                            >
                              {track.trackName}
                            </span>
                            <div
                              style={{
                                width: 30,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  height: 2,
                                  width: `${track.royaltyShare}%`,
                                  backgroundColor: "#5779FF",
                                  borderRadius: "3px 0px 0px 3px",
                                }}
                              />
                              <div
                                style={{
                                  height: 2,
                                  width: `${100 - (track.royaltyShare || 0)}%`,
                                  backgroundColor: "#2D334E",
                                  borderRadius: "0px 3px 3px 0px",
                                }}
                              />
                            </div>
                            <span
                              style={{ width: 50, textAlign: "end" }}
                              className="ellipsis"
                            >
                              {track.royaltyShare}
                            </span>
                            <span
                              style={{
                                marginLeft: -8,
                              }}
                            >
                              {" "}
                              %
                            </span>
                          </div>
                        ))}
                      </div>
                    ),
                  )}
              </div>
            </div>
            <div>
              <span
                style={{ color: "#F2F2F2", display: "block", marginBottom: 12 }}
              >
                {t("pages.Adiantamentos.valoresAte")}{" "}
                <span style={{ color: "#B3B3B3" }}>
                  {moment(advance.validUntil).format("DD/MM/YYYY")}
                </span>
              </span>

              {/* <p className="disclaimer">
                <Trans
                  i18nKey={t("pages.Adiantamentos.valoresAlteradosTexto")}
                />
              </p> */}
            </div>
          </div>
        </div>
      </div>
      <div className="footer black" style={{ paddingLeft: 0 }}>
        <div
          style={{
            display: "flex",
            width: isMobile ? "100%" : 650,
            flexDirection: "column",
            gap: 12,
          }}
        >
          {!showQuotes && (
            <button
              className="botao-roxo continuar"
              onClick={() => setShowSteps(true)}
            >
              {t("pages.Adiantamentos.continuar")}
            </button>
          )}
          <button
            className="botao-borda-roxa "
            onClick={onClickBackStep}
            style={{ width: "100%" }}
          >
            {t("pages.Adiantamentos.voltar")}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdiantamentoAceite;
